import {useId} from 'react';

import {cn} from '../../lib/utils';

import type {ReactNode} from 'react';

const SIZE = 480;
const RADII_RATIOS = [0.1, 0.2, 0.317, 0.433, 0.55, 0.683, 0.817];

export interface Ring {
  /** Ring radius as a fraction of `size` */
  ratio: number;
  /** Opacity 0–1. Default: 1 */
  opacity?: number;
}

export interface DecorativeCirclesProps {
  children: ReactNode;
  /** SVG canvas size in px. Rings scale proportionally. Default: 480 */
  size?: number;
  /** Ring stroke color. Defaults to var(--border-subtle) */
  color?: string;
  /** Ring stroke width in px. Default: 1 */
  strokeWidth?: number;
  /**
   * Custom ring definitions. When provided, replaces the default 7-ring
   * pattern and disables the radial gradient mask so each ring's opacity
   * is controlled individually.
   */
  rings?: Ring[];
  className?: string;
}

export function DecorativeCircles({
  children,
  size = SIZE,
  color = 'var(--border-subtle)',
  strokeWidth = 1,
  rings,
  className
}: DecorativeCirclesProps) {
  const id = useId();
  const gradientId = `decorative-circles-gradient-${id}`;
  const maskId = `decorative-circles-mask-${id}`;
  const center = size / 2;

  const useGradientMask = rings === undefined;
  const activeRings: Ring[] = rings ?? RADII_RATIOS.map(ratio => ({ratio}));

  return (
    <div data-slot="decorative-circles" className={cn('relative', className)}>
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        width={size}
        height={size}
        fill="none"
      >
        {useGradientMask && (
          <defs>
            <radialGradient id={gradientId}>
              <stop offset="30%" stopColor="white" />
              <stop offset="100%" stopColor="black" />
            </radialGradient>
            <mask id={maskId}>
              <rect width={size} height={size} fill={`url(#${gradientId})`} />
            </mask>
          </defs>
        )}
        <g mask={useGradientMask ? `url(#${maskId})` : undefined}>
          {activeRings.map(({ratio, opacity}, i) => (
            <circle
              key={i}
              cx={center}
              cy={center}
              r={Math.round(ratio * size)}
              stroke={color}
              strokeWidth={strokeWidth}
              opacity={opacity}
            />
          ))}
        </g>
      </svg>
      <div className="relative z-10">{children}</div>
    </div>
  );
}
